import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCalendarCheck, FaUser, FaPhone, FaMapMarkerAlt, FaCity, FaArrowLeft, FaCompass } from 'react-icons/fa';
import { BASE_URL } from '../Utils/Function';
import BackgroundEffect from './BackgroundEffect';
import homeAsset from '../assets/HOme.png';

const Booking = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [validated, setValidated] = useState(false);

    const serviceImage = location.state?.serviceImage;
    const fullImageUrl = serviceImage
        ? (serviceImage.startsWith('http') ? serviceImage : `${BASE_URL}/${serviceImage}`)
        : null;

    const [formData, setFormData] = useState({
        user_id: '',
        user_name: '',
        mobile_no: '',
        serv_name: location.state?.serviceName || '',
        address: '',
        town: ''
    });

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            setFormData(prev => ({
                ...prev,
                user_id: userData.user_id || '',
                user_name: prev.user_name || userData.user_name || '',
                mobile_no: prev.mobile_no || userData.mobile_no || ''
            }));
        }
    }, []);

    const handleSubmit = async (e) => {
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.preventDefault();
            e.stopPropagation();
            setValidated(true);
            return;
        }

        e.preventDefault();
        setValidated(true);
        setIsLoading(true);
        setError('');
        setSuccessMsg('');

        try {
            const response = await axios.post(`${BASE_URL}/booking/createBooking.php`, formData);

            if (response.data.status === 201 || response.data.success) {
                setSuccessMsg('Booking Request Architected Successfully!');
                setTimeout(() => {
                    navigate('/booking-history');
                }, 2500);
            } else {
                setError(response.data.message || 'Restructuring Booking Failed');
            }
        } catch (err) {
            setError('System Error: ' + (err.response?.data?.message || 'Connection Interrupted'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="booking-immersive-page">
            <BackgroundEffect />

            {/* Background Decorative Asset */}
            <img src={homeAsset} alt="" className="booking-bg-asset" />

            <div className="container position-relative" style={{ zIndex: 2 }}>
                {/* Back Button */}
                <div className="pt-4 pt-md-5">
                    <button
                        className="btn-back-minimal d-flex align-items-center gap-2"
                        onClick={() => navigate(-1)}
                    >
                        <FaArrowLeft /> RETURN TO SERVICES
                    </button>
                </div>

                <div className="booking-layout-grid py-4">
                    {/* Left Side: Service Visualization */}
                    <div className="booking-visual-panel">
                        <div className="visual-header mb-4">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img src={homeAsset} alt="Logo" height="50" style={{ filter: 'brightness(1.5)' }} />
                                <span className="section-index m-0">AL-WASEEQ DESIGN</span>
                            </div>
                            <span className="section-index-tiny">PROJECT SPECIFICATION</span>
                            <h1 className="display-5 fw-lighter mt-2 text-uppercase" style={{ letterSpacing: '-1px', color: 'var(--secondary-color)' }}>
                                {formData.serv_name}
                            </h1>
                        </div>

                        {fullImageUrl && (
                            <div className="service-hero-frame mb-4 d-flex align-items-center justify-content-center">
                                <img src={fullImageUrl} alt={formData.serv_name} className="w-100 h-100" style={{ objectFit: 'contain' }} />
                                <div className="frame-border"></div>
                            </div>
                        )}

                        <div className="booking-meta-info d-none d-lg-block">
                            <div className="d-flex align-items-center gap-3 mb-3 text-white-50">
                                <FaCompass />
                                <span className="small letter-spacing-2">AL-WASEEQ DESIGN PROTOCOL</span>
                            </div>
                            <p className="small text-white-50 opacity-50">
                                Initiating architectural consultation sequence. All parameters are locked to secure transmission channels.
                            </p>
                        </div>
                    </div>

                    {/* Right Side: Form Content */}
                    <div className="booking-form-panel">
                        <div className="form-inner-boundary">
                            <div className="mb-5">
                                <h2 className="section-index mb-2" style={{ color: 'var(--secondary-color)' }}>BOOKING REGISTRATION</h2>
                                <p className="small text-white-50">Please define the structural requirements for your upcoming project.</p>
                            </div>

                            {error && <div className="alert-arch error mb-4">{error}</div>}
                            {successMsg && <div className="alert-arch success mb-4">{successMsg}</div>}

                            <form onSubmit={handleSubmit} noValidate className={`row g-3 g-md-4 ${validated ? 'was-validated' : ''}`}>
                                <div className="col-12">
                                    <div className="arch-blueprint-group">
                                        <label className="section-index-tiny">FULL LEGAL NAME</label>
                                        <div className="blueprint-input-wrapper">
                                            <FaUser className="input-icon" />
                                            <input
                                                type="text"
                                                className="arch-blueprint-input-alt"
                                                required
                                                readOnly
                                                value={formData.user_name}
                                                placeholder="Enter Name"
                                            />
                                            <div className="invalid-feedback-arch">Structural identification required</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="arch-blueprint-group">
                                        <label className="section-index-tiny">MOBILE CONTACT</label>
                                        <div className="blueprint-input-wrapper">
                                            <FaPhone className="input-icon" />
                                            <input
                                                type="text"
                                                className="arch-blueprint-input-alt"
                                                required
                                                readOnly
                                                value={formData.mobile_no}
                                                placeholder="Enter Phone Number"
                                            />
                                            <div className="invalid-feedback-arch">Mobile link required</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="arch-blueprint-group">
                                        <label className="section-index-tiny">PROJECT TOWN</label>
                                        <div className="blueprint-input-wrapper">
                                            <FaCity className="input-icon" />
                                            <input
                                                type="text"
                                                className="arch-blueprint-input-alt"
                                                required
                                                disabled={isLoading}
                                                value={formData.town}
                                                onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                                                placeholder="Town / City"
                                            />
                                            <div className="invalid-feedback-arch">Location parameter required</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="arch-blueprint-group">
                                        <label className="section-index-tiny">SITE SPECIFICATIONS (ADDRESS)</label>
                                        <div className="blueprint-input-wrapper">
                                            <FaMapMarkerAlt className="input-icon" style={{ top: '20px' }} />
                                            <textarea
                                                className="arch-blueprint-input-alt"
                                                required
                                                disabled={isLoading}
                                                style={{ height: '100px', paddingTop: '15px' }}
                                                value={formData.address}
                                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                                placeholder="Complete building address"
                                            />
                                            <div className="invalid-feedback-arch">Site specifications required</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 pt-4">
                                    <button className="btn-gold w-100 py-3 d-flex align-items-center justify-content-center gap-3" type="submit" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                BOOKING...
                                            </>
                                        ) : (
                                            <>
                                                <FaCalendarCheck />
                                                BOOK NOW
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .booking-immersive-page {
                    min-height: 100vh;
                    background: #050505;
                    color: #fff;
                    position: relative;
                    overflow-x: hidden;
                    padding-bottom: 50px;
                    padding-top: 80px;
                }

                .booking-bg-asset {
                    position: fixed;
                    bottom: 5%;
                    right: 5%;
                    width: clamp(100px, 15vw, 200px);
                    height: auto;
                    opacity: 0.1;
                    pointer-events: none;
                    filter: grayscale(1);
                    z-index: 1;
                }

                .btn-back-minimal {
                    background: transparent;
                    border: none;
                    color: rgba(255,255,255,0.4);
                    font-size: 0.65rem;
                    letter-spacing: 3px;
                    transition: all 0.3s ease;
                    padding: 0;
                    text-transform: uppercase;
                }

                .btn-back-minimal:hover {
                    color: var(--secondary-color);
                    transform: translateX(-5px);
                }

                .booking-layout-grid {
                    display: grid;
                    grid-template-columns: 1fr 1.2fr;
                    gap: 80px;
                    align-items: start;
                }

                .service-hero-frame {
                    position: relative;
                    height: 320px;
                    background: rgba(255,255,255,0.01);
                    border: 1px solid rgba(255,255,255,0.05);
                    overflow: hidden;
                    border-radius: 4px;
                    padding: 20px;
                }

                .frame-border {
                    position: absolute;
                    inset: 12px;
                    border: 1px solid var(--secondary-color);
                    opacity: 0.15;
                    pointer-events: none;
                }

                .arch-blueprint-group {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .section-index-tiny {
                    font-size: 0.55rem;
                    letter-spacing: 3px;
                    color: var(--secondary-color);
                    font-weight: 500;
                    text-transform: uppercase;
                }

                .blueprint-input-wrapper {
                    position: relative;
                }

                .input-icon {
                    position: absolute;
                    left: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--secondary-color);
                    opacity: 0.5;
                    font-size: 0.9rem;
                }

                .arch-blueprint-input-alt {
                    width: 100%;
                    background: linear-gradient(90deg, rgba(255,255,255,0.02), transparent);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #fff;
                    padding: 18px 20px 18px 55px;
                    font-size: 0.95rem;
                    font-weight: 300;
                    outline: none;
                    transition: all 0.3s ease;
                }

                .arch-blueprint-input-alt:focus {
                    border-color: var(--secondary-color);
                    background: rgba(197, 160, 89, 0.05);
                    box-shadow: 0 0 15px rgba(197, 160, 89, 0.1);
                }

                .arch-blueprint-input-alt[readonly] {
                    background: rgba(255, 255, 255, 0.01);
                    cursor: not-allowed;
                    opacity: 0.7;
                    border-color: rgba(255, 255, 255, 0.05);
                }

                form.was-validated .arch-blueprint-input-alt:invalid {
                    border-color: #dc3545;
                }
                
                form.was-validated .arch-blueprint-input-alt:valid {
                    border-color: #28a745;
                }

                .invalid-feedback-arch {
                    display: none;
                    width: 100%;
                    margin-top: 5px;
                    font-size: 0.55rem;
                    color: #dc3545;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                form.was-validated .arch-blueprint-input-alt:invalid ~ .invalid-feedback-arch {
                    display: block;
                }

                .alert-arch {
                    padding: 20px;
                    font-size: 0.75rem;
                    letter-spacing: 1px;
                    border-left: 3px solid;
                }
                .alert-arch.error { border-color: #dc3545; color: #dc3545; background: rgba(220, 53, 69, 0.05); }
                .alert-arch.success { border-color: #28a745; color: #28a745; background: rgba(40, 167, 69, 0.05); }

                @media (max-width: 1200px) {
                    .booking-layout-grid { gap: 40px; }
                }

                @media (max-width: 991px) {
                    .booking-layout-grid {
                        grid-template-columns: 1fr;
                        gap: 50px;
                    }
                    .service-hero-frame { height: clamp(200px, 40vh, 320px); }
                    .booking-immersive-page { padding-top: 80px; }
                    .visual-header { text-align: center; }
                    .visual-header div { justify-content: center; }
                    .form-inner-boundary { max-width: 600px; margin: 0 auto; }
                    .display-5 { font-size: clamp(1.8rem, 6vw, 2.5rem); }
                }

                @media (max-width: 576px) {
                    .booking-immersive-page { padding-top: 70px; }
                    .booking-layout-grid { gap: 30px; }
                    .arch-blueprint-input-alt { padding: 15px 15px 15px 45px; font-size: 0.85rem; }
                    .btn-gold { font-size: 0.8rem; padding: 12px !important; }
                    .section-index { font-size: 0.6rem; }
                    .section-index-tiny { font-size: 0.5rem; }
                }
            `}</style>
        </div>
    );
};

export default Booking;
